import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useOverlay } from 'src/providers/OverlayProvider';
import {
  SaleOrderReadExt,
  SaleOrderUpdateExt,
  useSaleOrderLineDelete,
  useSaleOrderQuery,
  useSaleOrderUpdate,
} from 'src/services/saleOrder';
import { useQueryClient } from '@tanstack/react-query';
import { SelectedProductsProduct } from 'src/components/layouts/SelectProducts/SelectProducts';

type PickerSelectionLine = {
  id?: string | number;
  product?: { id?: number | null } | null;
  product_id?: number | null;
};

export const mergeOrderLinesWithSelection = <T extends PickerSelectionLine>(
  currentLines: T[],
  products: SelectedProductsProduct[],
) => {
  const lineProductId = (line: PickerSelectionLine) => {
    const productId = line.product?.id ?? line.product_id;
    return productId != null ? Number(productId) : null;
  };

  const selectedProductIds = new Set(products.map(product => Number(product.product_id)));
  const existingProductIds = new Set(currentLines.map(lineProductId).filter(id => id != null));

  // An order may hold several lines of the same product (e.g. marketplace imports
  // create one line per feed line), while the picker only knows products. Keep
  // every line whose product is still selected instead of collapsing per product —
  // dropped lines get DELETEd on save and silently reduce the ordered quantity.
  // Lines without a resolvable product never appear in the picker, so the user
  // cannot deselect them; they must survive the merge as well.
  const keptLines = currentLines.filter(line => {
    const productId = lineProductId(line);
    return productId == null || selectedProductIds.has(productId);
  });

  const newLines = products
    .filter(product => !existingProductIds.has(Number(product.product_id)))
    .map(product => ({
      // negative temp id: cannot collide with a real sale.order.line id, which
      // the save handler relies on to tell new lines from existing ones
      id: -Number(product.product_id),
      quantity: 1,
      product: {
        ...product,
        id: product.product_id,
        tracking: product.tracking || 'none',
      },
      applied_quality_checks: [],
      discount: 0,
      unit_price: 0,
    }));

  return [...keptLines, ...newLines];
};

export const useOutboundOrder = () => {
  const { t } = useTranslation();
  const { id: idString } = useParams();
  const id = idString ? Number.parseInt(idString, 10) : NaN;
  const { onClose } = useOverlay();
  const queryClient = useQueryClient();

  if (Number.isNaN(id)) {
    throw new Error(`Invalid outbound ID: ${idString}`);
  }

  const { data: saleOrder, isLoading: loading } = useSaleOrderQuery(id);
  const updateMutation = useSaleOrderUpdate();
  const deleteOrderLineMutation = useSaleOrderLineDelete();

  const [saleOrderItem, setSaleOrderItem] = useState<SaleOrderUpdateExt>({} as any);
  const [edit, setEdit] = useState(false);
  const [showSelectProducts, setShowSelectProducts] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false);

  useEffect(() => {
    setSaleOrderItem({} as SaleOrderUpdateExt);
    setEdit(false);
    setShowSelectProducts(false);
    setIsCancelConfirmationOpen(false);
  }, [id]);

  useEffect(() => {
    if (saleOrder && !edit) {
      setSaleOrderItem({} as SaleOrderUpdateExt);
    }
  }, [saleOrder, edit]);

  const handleSaleOrderUpdate = async () => {
    try {
      const updatedDate = saleOrderItem.commitment_date ? new Date(saleOrderItem.commitment_date) : null;

      const partner = saleOrderItem.partner
        ? {
            name: saleOrderItem.partner.name,
            address: saleOrderItem.partner.address,
            phone: saleOrderItem.partner.phone,
            email: saleOrderItem.partner.email,
            company_name: saleOrderItem.partner.company_name,
            tax_id: saleOrderItem.partner.tax_id,
          }
        : undefined;

      const originalLineIds = new Set((saleOrder?.order_lines ?? []).map(l => Number(l.id)));
      const bufferedLines = saleOrderItem.order_lines;
      const transformedOrderLines =
        bufferedLines?.map(orderLine => {
          const isExisting = originalLineIds.has(Number(orderLine.id));
          return {
            ...(isExisting ? { id: orderLine.id } : {}),
            applied_quality_checks: orderLine.applied_quality_checks || [],
            discount: orderLine.discount || 0,
            lot_id: (orderLine as any).lot?.id || orderLine.lot_id || null,
            product_id: (orderLine as any).product?.id || orderLine.product_id,
            quantity: orderLine.quantity || 1,
            unit_price: orderLine.unit_price || 0,
          };
        }) || [];

      let removedLineIds: number[] = [];
      if (bufferedLines) {
        const keptIds = new Set(bufferedLines.map(l => Number(l.id)).filter(lineId => originalLineIds.has(lineId)));
        removedLineIds = Array.from(originalLineIds).filter(lineId => !keptIds.has(lineId));
      }

      await updateMutation.mutateAsync({
        params: {
          path: {
            sale_order_id: id,
          },
        },
        body: {
          ...saleOrderItem,
          commitment_date: updatedDate?.toISOString(),
          partner,
          order_lines: transformedOrderLines,
        },
      });

      if (removedLineIds.length > 0) {
        await Promise.all(
          removedLineIds.map(lineId =>
            deleteOrderLineMutation.mutateAsync({
              params: { path: { sale_order_line_id: lineId } },
            }),
          ),
        );
      }

      await queryClient.invalidateQueries({
        queryKey: ['get', '/sale_orders/{sale_order_id}', { params: { path: { sale_order_id: id } } }],
      });

      toast.success(t('success.update_sale_order_success'));
    } catch (error: any) {
      const detail = error?.detail || error?.message || '';
      let message = t('errors.update_sale_order_failed');
      if (typeof detail === 'string' && detail.includes('INCOTERM_NOT_ENABLED')) {
        message = t('errors.incoterm_not_enabled');
      } else if (typeof detail === 'string' && detail.includes('COD_NOT_ENABLED')) {
        message = t('errors.cod_not_enabled');
      }
      toast.error(message);
    }
  };

  // Merge patch-over-original like PrimaryInfo's `isCod`/`codAmount`/`codCurrencyId`
  // (`!== undefined`, not `??`): the edit form explicitly nulls cod_amount /
  // cod_currency_id when the COD toggle is switched off, and a save must
  // validate that effective (merged) state — not just whichever fields happen
  // to be present in the patch.
  const validateCodRequirements = (item: SaleOrderUpdateExt, order: SaleOrderReadExt | undefined): boolean => {
    const isCod = item.is_cod !== undefined ? item.is_cod : order?.is_cod ?? false;
    if (!isCod) return true;
    const codAmount = item.cod_amount !== undefined ? item.cod_amount : order?.cod_amount ?? null;
    const codCurrencyId = item.cod_currency_id !== undefined ? item.cod_currency_id : order?.cod_currency_id ?? null;
    return !!codAmount && codAmount > 0 && !!codCurrencyId;
  };

  const hasChanges = Object.keys(saleOrderItem).length > 0;

  const handleAction = async (action: 'cancel' | 'save') => {
    if (action !== 'save' && edit && hasChanges) {
      setIsCancelConfirmationOpen(true);
    } else if (action !== 'save' && edit && !hasChanges) {
      setEdit(false);
      setSaleOrderItem({} as SaleOrderUpdateExt);
    } else if (action !== 'save' && !edit) {
      onClose();
    } else if (action === 'save') {
      if (!validateCodRequirements(saleOrderItem, saleOrder as SaleOrderReadExt | undefined)) {
        toast.error(t('outbound.cod_validation'));
        return;
      }

      setEdit(false);
      await handleSaleOrderUpdate();
      if (saleOrderItem) {
        setSaleOrderItem({} as any);
      }
    }
  };

  const handleConfirmClose = (confirmed: boolean) => {
    if (confirmed) {
      setEdit(false);
      setSaleOrderItem({} as SaleOrderUpdateExt);
    }

    setIsCancelConfirmationOpen(false);
  };

  const handleOrderLineDelete = (orderLineId: number) => {
    const baseline = (saleOrderItem.order_lines ?? saleOrder?.order_lines ?? []) as any[];
    const filtered = baseline.filter(line => Number(line.id) !== orderLineId);
    setSaleOrderItem({ ...saleOrderItem, order_lines: filtered as any });
  };

  const updateSaleOrderItem = (key: string, value: any) => {
    setSaleOrderItem(prev => ({ ...prev, [key]: value }));
  };

  const handleProductsSelected = (products: SelectedProductsProduct[]) => {
    const currentLines = saleOrderItem.order_lines ?? saleOrder?.order_lines ?? [];
    const merged = mergeOrderLinesWithSelection(currentLines as PickerSelectionLine[], products);

    setSaleOrderItem({ ...saleOrderItem, order_lines: merged as any });
    setShowSelectProducts(false);
  };

  return {
    saleOrder,
    loading,
    saleOrderItem,
    edit,
    showSelectProducts,
    isCancelConfirmationOpen,
    handleAction,
    handleOrderLineDelete,
    updateSaleOrderItem,
    handleProductsSelected,
    setEdit,
    setShowSelectProducts,
    setIsCancelConfirmationOpen,
    handleConfirmClose,
    hasChanges,
  };
};
